/**
 * Phase 15 — idf_gap_analysis 10-dimension diagnostic panel.
 *
 * Stage 1 advisory panel. Visible only when Stage 1 is frozen — the
 * panel reads the matter's typed IDF summary + skill_guidance and
 * surfaces a 10-dimension readiness assessment (Core Invention →
 * Claim Drafting Readiness) before the drafter advances to Stage 2.
 *
 * No freeze gate changes — the panel is purely advisory. A drafter
 * who hits a "block" severity is expected to use it as a checklist
 * for upload/Q&A revisions, not as a hard halt.
 *
 * Cache: backend stores results for 24h on
 * stage_state.content.gap_analysis_cache. Subsequent renders return
 * cache_hit=true; the "Refresh" link forces a re-run (force_refresh).
 * Supplying prior-art context always re-runs (the context is part
 * of the prompt input, so cache must invalidate).
 */
import React, { useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Loader2,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import {
  runGapAnalysis,
  type GapAnalysisResult,
  type GapAnalysisDimension,
} from '@/api/client';

interface Props {
  matterId: string;
  /** Stage 1 frozen flag from the page-level state. The panel
   *  renders nothing when false (advisory-only contract). */
  isFrozen: boolean;
}

const SEVERITY_ICON: Record<GapAnalysisDimension['severity'], React.ReactNode> = {
  ok:    <CheckCircle2 size={16} className="text-emerald-600" />,
  warn:  <AlertTriangle size={16} className="text-amber-500" />,
  block: <XCircle size={16} className="text-rose-600" />,
};

const READINESS_CHIP: Record<GapAnalysisResult['overall_readiness'], string> = {
  ready:      'bg-emerald-100 text-emerald-800 border-emerald-300',
  needs_work: 'bg-amber-100 text-amber-800 border-amber-300',
  incomplete: 'bg-rose-100 text-rose-800 border-rose-300',
};

function _formatAge(cachedAtIso: string): string {
  if (!cachedAtIso) return '';
  const cached = new Date(cachedAtIso);
  const ageMs = Date.now() - cached.getTime();
  const ageH = ageMs / (1000 * 60 * 60);
  if (ageH < 1) return `${Math.round(ageH * 60)} min`;
  return `${Math.round(ageH)} h`;
}

export default function GapAnalysisPanel({ matterId, isFrozen }: Props) {
  const [result, setResult] = useState<GapAnalysisResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [priorArtVisible, setPriorArtVisible] = useState(false);
  const [priorArtText, setPriorArtText] = useState('');

  if (!isFrozen) return null;

  async function run(force = false) {
    setLoading(true); setError('');
    try {
      const opts: { prior_art_context?: string; force_refresh?: boolean } = {};
      if (priorArtText.trim()) opts.prior_art_context = priorArtText.trim();
      if (force) opts.force_refresh = true;
      const r = await runGapAnalysis(matterId, opts);
      setResult(r);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Gap analysis failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  function toggleDim(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  return (
    <div
      data-testid="gap-analysis-panel"
      className="border border-zinc-200 rounded-md bg-white p-4 mt-4"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-zinc-900">
          IDF Gap Analysis
          <span className="ml-2 text-xs font-normal text-zinc-500">
            10-dimension readiness check
          </span>
        </h3>
        {result && (
          <span
            data-testid="gap-analysis-readiness-badge"
            className={
              'inline-flex items-center px-2 py-0.5 text-[11px] font-medium ' +
              'border rounded-full ' + READINESS_CHIP[result.overall_readiness]
            }
          >
            {result.overall_readiness.replace('_', ' ')} · {result.overall_score}/5
          </span>
        )}
      </div>

      {!result && !loading && (
        <button
          type="button"
          onClick={() => run(false)}
          data-testid="gap-analysis-run-btn"
          className="px-3 py-1.5 text-xs font-medium border border-zinc-300 rounded hover:bg-zinc-50"
        >
          Analyse IDF
        </button>
      )}
      {loading && (
        <div className="flex items-center gap-2 text-xs text-zinc-600">
          <Loader2 size={14} className="animate-spin" />
          Running gap analysis…
        </div>
      )}
      {error && (
        <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 mb-2">
          {error}
        </div>
      )}

      {/* Prior-art context toggle */}
      {!loading && (
        <div className="mt-3">
          <button
            type="button"
            data-testid="gap-analysis-prior-art-toggle"
            onClick={() => setPriorArtVisible((v) => !v)}
            className="text-[11px] text-zinc-600 hover:text-zinc-900 underline"
          >
            {priorArtVisible
              ? 'Hide prior art context'
              : 'Add prior art context (optional)'}
          </button>
          {priorArtVisible && (
            <textarea
              data-testid="gap-analysis-prior-art-input"
              value={priorArtText}
              onChange={(e) => setPriorArtText(e.target.value)}
              placeholder="Paste any known prior art context, citations, or comparators here. Submitting always re-runs the analysis."
              rows={3}
              className="w-full mt-2 px-2 py-1 text-xs border border-zinc-300 rounded font-mono"
            />
          )}
        </div>
      )}

      {/* Cache hint + refresh */}
      {result && !loading && (
        <div className="text-[11px] text-zinc-500 mb-2">
          Results cached ({_formatAge(result.cached_at)} ago) ·{' '}
          <button
            type="button"
            onClick={() => run(true)}
            data-testid="gap-analysis-refresh-btn"
            className="inline-flex items-center gap-1 underline hover:text-zinc-900"
          >
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
      )}

      {/* 10-dimension rows */}
      {result && !loading && (
        <div
          className="border-t border-zinc-100 divide-y divide-zinc-100"
          data-testid="gap-analysis-dimensions"
        >
          {result.dimensions.map((d) => {
            const expanded = expandedIds.has(d.id);
            return (
              <div key={d.id} data-testid={`gap-analysis-dim-${d.id}`}>
                <button
                  type="button"
                  onClick={() => toggleDim(d.id)}
                  className="w-full flex items-center gap-2 px-1 py-2 text-left hover:bg-zinc-50"
                >
                  <span data-testid={`gap-analysis-dim-${d.id}-icon`}>
                    {SEVERITY_ICON[d.severity]}
                  </span>
                  <span className="text-xs font-medium text-zinc-800 flex-1">
                    {d.label}
                  </span>
                  <span className="text-[10px] font-mono text-zinc-500">
                    {d.score}/5
                  </span>
                  {expanded
                    ? <ChevronDown size={14} className="text-zinc-400" />
                    : <ChevronRight size={14} className="text-zinc-400" />}
                </button>
                {expanded && (
                  <div className="pb-3 pl-7 pr-2 text-xs text-zinc-700 space-y-1">
                    <div data-testid={`gap-analysis-dim-${d.id}-finding`}>
                      <span className="font-semibold">Finding: </span>
                      {d.finding || '—'}
                    </div>
                    {d.suggestion && (
                      <div data-testid={`gap-analysis-dim-${d.id}-suggestion`}>
                        <span className="font-semibold">Suggestion: </span>
                        {d.suggestion}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
